'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { getMobileNavEntries } from '@/lib/marketing-nav';
import { Button } from './button';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';

const NAV_ENTRIES = getMobileNavEntries();

export function MobileNav() {
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations();

  // `label` is the English fallback for links that intentionally carry no
  // messageKey (untranslated destinations — see src/lib/ui-copy.ts).
  const label = (item: { label: string; messageKey?: string }) =>
    item.messageKey ? t(item.messageKey) : item.label;

  const close = () => setIsOpen(false);

  useEffect(() => {
    if (!isOpen) return;
    const handleClick = () => close();
    document.addEventListener('click', handleClick);

    return () => document.removeEventListener('click', handleClick);
  }, [isOpen]);

  useEffect(() => {
    document.body.classList.toggle('nav-open', isOpen);

    return () => {
      document.body.classList.remove('nav-open');
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <>
      <button
        type="button"
        className="relative z-10 md:hidden flex size-11 items-center justify-center rounded-full text-white hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 focus-visible:ring-offset-2 focus-visible:ring-offset-black"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((prev) => !prev);
        }}
        aria-expanded={isOpen}
        aria-controls="mobile-nav-menu"
        aria-label={isOpen ? 'Close menu' : 'Open menu'}
      >
        {isOpen ? <X className="size-6" /> : <Menu className="size-6" />}
      </button>

      <div
        id="mobile-nav-menu"
        className={cn(
          'fixed inset-0 z-40 md:hidden',
          'bg-black/60 backdrop-blur-sm',
          'transition-opacity duration-200',
          isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        )}
        aria-hidden={!isOpen}
        onClick={close}
      />

      <div
        className={cn(
          'fixed top-0 right-0 z-50 h-full w-full max-w-sm md:hidden',
          'bg-black border-l border-white/10 shadow-2xl',
          'flex flex-col',
          'transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : 'translate-x-full'
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-1 pt-24 pb-8 px-6 overflow-y-auto">
          {NAV_ENTRIES.map((entry) =>
            entry.type === 'link' ? (
              <Link
                key={entry.item.href}
                href={entry.item.href}
                prefetch={entry.item.prefetch ?? true}
                className="rounded-lg px-4 py-3 text-lg font-bold text-white/90 hover:text-white hover:bg-white/10 transition-colors min-h-[44px] flex items-center"
                onClick={close}
              >
                {label(entry.item)}
              </Link>
            ) : (
              <details key={entry.heading} className="group">
                <summary className="flex min-h-[44px] cursor-pointer list-none items-center justify-between rounded-lg px-4 py-3 text-lg font-bold text-white/90 transition-colors hover:bg-white/10">
                  {entry.heading}
                  <svg
                    className="h-5 w-5 flex-shrink-0 text-white/50 transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    aria-hidden
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <div className="flex flex-col gap-1 pb-2 pl-3">
                  {entry.items.map(({ href, label: itemLabel, messageKey, prefetch }) => (
                    <Link
                      key={href}
                      href={href}
                      prefetch={prefetch ?? false}
                      className="flex min-h-[44px] items-center rounded-lg px-4 py-2 text-base font-semibold text-white/70 transition-colors hover:bg-white/10 hover:text-white"
                      onClick={close}
                    >
                      {messageKey ? t(messageKey) : itemLabel}
                    </Link>
                  ))}
                </div>
              </details>
            )
          )}
        </div>
        <div className="mt-auto p-6 pt-4 border-t border-white/10 flex flex-col gap-3">
          <Link href="/sign-in" className="block" onClick={close}>
            <Button
              variant="secondary"
              size="md"
              className="w-full h-12 text-base font-medium bg-transparent border border-white/20 hover:border-white/40 text-white hover:bg-white/5"
            >
              {t('nav.login')}
            </Button>
          </Link>
          <Link href="/sign-up" className="block" onClick={close}>
            <Button
              variant="primary"
              size="md"
              className="w-full h-12 text-base font-semibold bg-gradient-to-r from-[#8B5CF6] via-[#EC4899] to-[#F59E0B] hover:from-[#7C3AED] hover:via-[#DB2777] hover:to-[#D97706] text-white border-0"
            >
              {t('nav.primaryCta')}
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
}
